import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Facebook, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const details = [
  { icon: MapPin, label: "Visit Us", value: "701 Phillips Place, Suite 5, Huntsville, AR", testid: "contact-address" },
  { icon: Clock, label: "Hours", value: "Mon – Fri, 9:00 AM – 2:00 PM · Walk-ins & appointments welcome", testid: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch soon. You're not alone.");
      setForm({ name: "", contact: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at 479-437-5939 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="honeycomb-light relative bg-cream-deep py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-gold-deep" data-testid="contact-eyebrow">
            Chapter 06 — Find The Hive
          </p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="contact-heading">
            Come as you are. <span className="italic text-gold-deep">Leave connected.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Info + form */}
          <Reveal>
            <div className="flex h-full flex-col gap-8">
              <div className="space-y-5">
                {details.map((d) => (
                  <div key={d.label} className="flex items-start gap-4" data-testid={d.testid}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-gold/40 bg-white text-gold-deep">
                      <d.icon size={19} strokeWidth={1.9} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-deep">{d.label}</p>
                      <p className="mt-1 text-sm font-semibold leading-relaxed text-ink">{d.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:4794375939"
                  data-testid="contact-call-btn"
                  className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-cream transition-colors duration-300 hover:bg-gold-deep"
                >
                  <Phone size={16} /> Call 479-437-5939
                </a>
                <a
                  href="mailto:Themadcohive@gmail.com"
                  data-testid="contact-email-btn"
                  className="flex items-center gap-2 rounded-full border-2 border-ink/70 px-6 py-2.5 text-sm font-bold text-ink transition-colors duration-300 hover:border-gold-deep hover:text-gold-deep"
                >
                  <Mail size={16} /> Themadcohive@gmail.com
                </a>
                <a
                  href="https://www.facebook.com/share/1CE1uxViXo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  className="flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-sm font-bold text-gold-deep transition-colors duration-300 hover:bg-gold hover:text-white"
                >
                  <Facebook size={16} /> Facebook
                </a>
              </div>

              <form onSubmit={submit} className="soft-shadow rounded-[1.75rem] border border-gold/20 bg-white p-7" data-testid="contact-form">
                <p className="font-display text-xl font-semibold text-ink">Send us a message</p>
                <p className="mt-1 text-xs font-semibold text-ink-soft">
                  Every message goes straight to The Hive team. Confidential, always.
                </p>
                <div className="mt-5 space-y-4">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="form-name-input"
                    className="w-full rounded-xl border border-input bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink-soft/50 focus:border-gold"
                  />
                  <input
                    required
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="Phone or email — how should we reach you?"
                    data-testid="form-contact-input"
                    className="w-full rounded-xl border border-input bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink-soft/50 focus:border-gold"
                  />
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help? (Share as much or as little as you like.)"
                    data-testid="form-message-input"
                    className="w-full resize-none rounded-xl border border-input bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink-soft/50 focus:border-gold"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="form-submit-btn"
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-gold-deep disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
                    {sending ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </Reveal>

          {/* Map */}
          <Reveal delay={0.15}>
            <div className="gold-ring h-full min-h-[420px] overflow-hidden rounded-[2rem] border border-gold/30" data-testid="map-frame">
              <iframe
                title="The Hive — 701 Phillips Place, Suite 5, Huntsville, AR"
                src="https://maps.google.com/maps?q=701%20Phillips%20Place%2C%20Huntsville%2C%20AR%2072740&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="h-full min-h-[420px] w-full"
                loading="lazy"
                data-testid="map-iframe"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
